import { useCallback, useEffect, useRef, useState } from 'react'
import { type User } from 'oidc-client-ts'
import {
  login,
  logout,
  getUser,
  getEmployeeIdentifier,
  handleCallback,
  callWithFreshToken,
  startTokenKeeper,
  markOfflineAccessAbgelehnt,
  SessionExpiredError,
  SESSION_EXPIRED_MESSAGE,
  userManager,
} from '@/lib/auth'
import {
  getPersonalCheckinStatus,
  listPersonalAuftraege,
  createPersonalRapport,
  type CreatePersonalRapportRequest,
  type CheckinStatusResponse,
  type ApiAuftrag,
} from '@/lib/api'
import { getPersonalEntries, type PersonalOutboxEntry } from '@/lib/db'
import {
  PERSONAL_OUTBOX_EVENT,
  applyLocalEntries,
  entryLines,
  flushPersonalOutbox,
  recordPersonalCheckin,
} from '@/lib/personalOutbox'
import { PersonalOutboxBanner } from '@/components/PersonalOutboxBanner'
import { RapportBereich } from '@/components/RapportBereich'
import {
  angebotGueltig,
  ladeRapportSpeicher,
  merkeAusstempeln,
  merkeEinstempeln,
  type RapportAngebot,
} from '@/lib/rapport'

const FLUSH_INTERVAL_MS = 15_000

function formatDuration(fromIso: string): string {
  const ms = Date.now() - new Date(fromIso).getTime()
  const totalMin = Math.floor(ms / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}h ${m}min` : `${m}min`
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

const ZUSATZGERAET_VORSCHLAEGE = [
  'Rüttelplatte',
  'Stampfer',
  'Anbauverdichter',
  'Sieblöffel',
  'Meißel',
]

const HANDLE_SIZE = 52
const TRACK_PAD = 4
const CONFIRM_THRESHOLD = 0.82

interface SlideButtonProps {
  onConfirm: () => void
  label: string
  color?: string
}

function SlideButton({ onConfirm, label, color = '#16a34a' }: SlideButtonProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [frac, setFrac] = useState(0)
  const dragging = useRef(false)
  const startClientX = useRef(0)
  const startFrac = useRef(0)

  function maxTravel() {
    const w = trackRef.current?.clientWidth ?? 280
    return w - HANDLE_SIZE - TRACK_PAD * 2
  }

  function clamp(x: number) {
    return Math.max(0, Math.min(1, x))
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    dragging.current = true
    startClientX.current = e.clientX
    startFrac.current = frac
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragging.current) return
    const delta = e.clientX - startClientX.current
    setFrac(clamp(startFrac.current + delta / maxTravel()))
  }

  function onPointerUp() {
    if (!dragging.current) return
    dragging.current = false
    if (frac >= CONFIRM_THRESHOLD) {
      setFrac(0)
      onConfirm()
    } else {
      setFrac(0)
    }
  }

  const handleLeft = TRACK_PAD + frac * maxTravel()
  const fillWidth = handleLeft + HANDLE_SIZE / 2

  return (
    <div
      ref={trackRef}
      style={{
        position: 'relative',
        height: HANDLE_SIZE + TRACK_PAD * 2,
        background: '#1e293b',
        borderRadius: 999,
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: fillWidth,
          background: color,
          borderRadius: 999,
          transition: dragging.current ? 'none' : 'width 0.2s ease',
          opacity: 0.35 + frac * 0.65,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          fontWeight: 600,
          color: frac > 0.4 ? '#fff' : '#94a3b8',
          pointerEvents: 'none',
          transition: 'color 0.15s',
        }}
      >
        {frac >= CONFIRM_THRESHOLD ? '✓' : label}
      </div>
      <div
        style={{
          position: 'absolute',
          top: TRACK_PAD,
          left: handleLeft,
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          borderRadius: '50%',
          background: color,
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          transition: dragging.current ? 'none' : 'left 0.2s ease',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        →
      </div>
    </div>
  )
}

type View = 'main' | 'kommen-auftrag' | 'gehen-confirm'

export function MeinPage() {
  const [user, setUser] = useState<User | null | 'loading'>('loading')
  const [employeeId, setEmployeeId] = useState<string | null>(null)
  const [status, setStatus] = useState<CheckinStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [, setNow] = useState(Date.now())
  const [view, setView] = useState<View>('main')
  const [auftraege, setAuftraege] = useState<ApiAuftrag[]>([])
  const [selectedAuftragId, setSelectedAuftragId] = useState<string>('')
  const [taetigkeit, setTaetigkeit] = useState('')
  const [zusatzgeraetChips, setZusatzgeraetChips] = useState<string[]>([])
  const [zusatzgeraetFreitext, setZusatzgeraetFreitext] = useState('')
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tokenRef = useRef<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [outbox, setOutbox] = useState<PersonalOutboxEntry[]>([])
  // Rapport auf Klick (#1151): reine Zusatzanzeige nach dem Ausstempeln.
  const [rapportAngebot, setRapportAngebot] = useState<RapportAngebot | null>(null)

  useEffect(() => {
    if (employeeId) setRapportAngebot(ladeRapportSpeicher(employeeId).angebot ?? null)
  }, [employeeId])

  useEffect(() => {
    async function init() {
      const params = new URLSearchParams(window.location.search)
      let resolved: User | null

      if (params.get('error') === 'invalid_scope') {
        // Keycloak lehnt offline_access ab: einmalig ohne den Scope neu anmelden.
        markOfflineAccessAbgelehnt()
        window.history.replaceState({}, '', '/')
        await login()
        return
      }

      if (params.has('code')) {
        resolved = await handleCallback()
      } else {
        resolved = await getUser()
      }

      setUser(resolved)

      if (resolved) {
        tokenRef.current = resolved.access_token
        const id = getEmployeeIdentifier(resolved)
        setEmployeeId(id)
        if (id) {
          loadStatus()
          loadAuftraege()
        }
      }
    }
    init()
  }, [])

  // Silent Renew liefert ein neues Token: tokenRef nachziehen.
  useEffect(() => {
    const onUserLoaded = (u: User) => {
      tokenRef.current = u.access_token
    }
    userManager.events.addUserLoaded(onUserLoaded)
    return () => userManager.events.removeUserLoaded(onUserLoaded)
  }, [])

  // Token aktiv frisch halten (Start, Timer, Sichtbarkeit, online) -- nicht erst bei 401.
  useEffect(() => {
    if (!user || user === 'loading') return
    return startTokenKeeper(() => setSessionExpired(true))
  }, [user])

  useEffect(() => {
    timerRef.current = setInterval(() => setNow(Date.now()), 30_000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const loadStatus = useCallback(async () => {
    if (!tokenRef.current) return
    try {
      const s = await callWithFreshToken((t) => {
        tokenRef.current = t
        return getPersonalCheckinStatus(t)
      })
      setStatus(s)
    } catch {
      // still ok
    }
  }, [])

  const refreshOutbox = useCallback(async () => {
    try {
      setOutbox(await getPersonalEntries())
    } catch {
      // IndexedDB nicht verfuegbar: keine Anzeige, Stempeln faellt auf direktes Senden zurueck
    }
  }, [])

  // Offline-Puffer senden: beim Oeffnen, online, Sichtbarkeits-Wechsel, Intervall (Hauptthread --
  // der Service Worker hat kein Token). Anzeige laedt bei jeder Aenderung der Outbox neu.
  useEffect(() => {
    if (!user || user === 'loading' || !employeeId) return
    let cancelled = false
    const flush = async () => {
      try {
        const result = await flushPersonalOutbox(employeeId)
        if (cancelled) return
        if (result.sessionExpired) setSessionExpired(true)
        if (result.sent + result.konflikt > 0) void loadStatus()
      } catch {
        // IndexedDB nicht verfuegbar -- nichts zu senden
      }
      void refreshOutbox()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush()
    }
    const onChanged = () => void refreshOutbox()
    void refreshOutbox()
    void flush()
    const timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS)
    window.addEventListener('online', flush)
    window.addEventListener(PERSONAL_OUTBOX_EVENT, onChanged)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('online', flush)
      window.removeEventListener(PERSONAL_OUTBOX_EVENT, onChanged)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [user, employeeId, loadStatus, refreshOutbox])

  async function loadAuftraege() {
    if (!tokenRef.current) return
    try {
      const list = await callWithFreshToken((t) => {
        tokenRef.current = t
        return listPersonalAuftraege(t)
      })
      setAuftraege(list)
    } catch {
      // Auftraege nicht verfuegbar
    }
  }

  function resetForm() {
    setSelectedAuftragId('')
    setTaetigkeit('')
    setZusatzgeraetChips([])
    setZusatzgeraetFreitext('')
    setView('main')
  }

  async function submit(
    logType: 'IN' | 'OUT' | 'ANFAHRT' | 'ABFAHRT',
    auftragId?: string,
    taetigkeitGeplant?: string,
    taetigkeitIst?: string,
    zusatzgeraetVal?: string,
  ) {
    if (!employeeId) return
    setError(null)
    try {
      // Schreibt sofort lokal (IndexedDB) und sendet im Hintergrund -- der Stempel ist mit dem
      // Tippen erfasst, auch ohne Netz. Die Statuszeile zeigt "wird gesendet" / "Gesendet".
      await recordPersonalCheckin(employeeId, logType, {
        auftragId: auftragId || undefined,
        taetigkeitGeplant: taetigkeitGeplant || undefined,
        taetigkeitIst: taetigkeitIst || undefined,
        zusatzgeraet: zusatzgeraetVal || undefined,
      })
      void refreshOutbox()
      if (logType === 'IN') {
        const a = auftraege.find((x) => x.auftragId === auftragId)
        merkeEinstempeln(
          employeeId,
          a ? { auftragId: a.auftragId, auftragsnummer: a.auftragsnummer, auftraggeberName: a.auftraggeberName } : null,
        )
        setRapportAngebot(null)
      } else if (logType === 'OUT') {
        setRapportAngebot(merkeAusstempeln(employeeId))
      }
      resetForm()
      loadStatus()
    } catch (e) {
      // Nur im Fallback ohne IndexedDB (direktes Senden): Eingaben bleiben im Formular.
      if (e instanceof SessionExpiredError) setSessionExpired(true)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  function erstelleRapport(req: CreatePersonalRapportRequest) {
    return callWithFreshToken((t) => {
      tokenRef.current = t
      return createPersonalRapport(t, req)
    })
  }

  function toggleZusatzgeraet(item: string) {
    setZusatzgeraetChips((prev) =>
      prev.includes(item) ? prev.filter((p) => p !== item) : [...prev, item],
    )
  }

  function zusatzgeraetAktiv(item: string) {
    return zusatzgeraetChips.includes(item)
  }

  function zusatzgeraetKombiniert() {
    const freitextTeile = zusatzgeraetFreitext
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    return [...zusatzgeraetChips, ...freitextTeile].join(', ')
  }

  // --- Loading ---
  if (user === 'loading') {
    return (
      <div className="screen">
        <div className="card" style={{ textAlign: 'center' }}>
          <p className="hint">Laden …</p>
        </div>
      </div>
    )
  }

  // --- Nicht eingeloggt ---
  if (!user || !employeeId) {
    return (
      <div className="screen">
        <div className="card">
          <h1>Mein Stempel</h1>
          {!user ? (
            <>
              <p className="hint">Mit deinem Glasbrenner-Account anmelden</p>
              <button className="button button-in" onClick={login}>
                Anmelden
              </button>
            </>
          ) : (
            <>
              <p className="hint">
                Dein Account hat keine Mitarbeiter-ID hinterlegt. Bitte Patric kontaktieren.
              </p>
              <button className="button button-secondary" onClick={logout}>
                Abmelden
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  if (sessionExpired) {
    return (
      <div className="screen">
        <div className="card">
          <h1>Mein Stempel</h1>
          <p className="hint">{SESSION_EXPIRED_MESSAGE}</p>
          {outbox.some((e) => e.status === 'pending') && (
            <p className="hint">Gespeicherte Stempel bleiben erhalten und werden nach der Anmeldung gesendet.</p>
          )}
          <button className="button button-in" onClick={login}>
            Anmelden
          </button>
        </div>
      </div>
    )
  }

  // Lokal noch ausstehende Kommen/Gehen-Stempel gelten sofort (auch offline).
  const effectiveStatus = applyLocalEntries(status, outbox, employeeId)
  const isCheckedIn = effectiveStatus?.is_checked_in ?? false
  const lastTime = effectiveStatus?.last_time ?? null
  const pendingCount = outbox.filter((e) => e.status === 'pending').length
  const lines = entryLines(outbox)
  const displayName = (user.profile.name ?? user.profile.preferred_username ?? employeeId) as string

  // --- Kommen: Auftrag + Tätigkeit + Zusatzgerät ---
  if (view === 'kommen-auftrag') {
    return (
      <div className="screen">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <button
              className="button button-secondary"
              style={{ fontSize: 13, padding: '6px 10px' }}
              onClick={resetForm}
            >
              ← Zurück
            </button>
            <h2 style={{ margin: 0, fontSize: 18 }}>Kommen</h2>
          </div>

          {error && <p className="error-text">{error}</p>}

          {/* Projekt */}
          {auftraege.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projekt</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {auftraege.map((a) => (
                  <button
                    key={a.auftragId}
                    className={`button ${selectedAuftragId === a.auftragId ? 'button-in' : 'button-secondary'}`}
                    style={{ textAlign: 'left', padding: '10px 14px' }}
                    onClick={() => setSelectedAuftragId((prev) => (prev === a.auftragId ? '' : a.auftragId))}
                  >
                    <div style={{ fontWeight: 600 }}>{a.auftragsnummer}</div>
                    <div style={{ fontSize: 13, opacity: 0.8 }}>
                      {a.auftraggeberName}{a.auftragsort ? ` · ${a.auftragsort}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tätigkeit */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ausgeführte Tätigkeit <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </p>
            <input
              className="input"
              type="text"
              placeholder="z.B. Erdarbeiten, Abbruch, Transport …"
              value={taetigkeit}
              onChange={(e) => setTaetigkeit(e.target.value)}
            />
          </div>

          {/* Zusatzgerät */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Zusatzgerät / Anbauteil <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {ZUSATZGERAET_VORSCHLAEGE.map((item) => (
                <button
                  key={item}
                  onClick={() => toggleZusatzgeraet(item)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    border: `1px solid ${zusatzgeraetAktiv(item) ? '#22c55e' : '#334155'}`,
                    background: zusatzgeraetAktiv(item) ? '#14532d' : '#1e293b',
                    color: zusatzgeraetAktiv(item) ? '#86efac' : '#94a3b8',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            <input
              className="input"
              type="text"
              placeholder="Weitere oder Freitext …"
              value={zusatzgeraetFreitext}
              onChange={(e) => setZusatzgeraetFreitext(e.target.value)}
            />
          </div>

          <button
            className="button button-in button-primary-action"
            onClick={() => submit('IN', selectedAuftragId, taetigkeit, undefined, zusatzgeraetKombiniert())}
          >
            {selectedAuftragId
              ? `Einstempeln — ${auftraege.find((a) => a.auftragId === selectedAuftragId)?.auftragsnummer ?? ''}`
              : 'Einstempeln'}
          </button>
        </div>
      </div>
    )
  }

  // --- Gehen: Tätigkeit was gemacht + Zusatzgerät bestätigen ---
  if (view === 'gehen-confirm') {
    return (
      <div className="screen">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <button
              className="button button-secondary"
              style={{ fontSize: 13, padding: '6px 10px' }}
              onClick={resetForm}
            >
              ← Zurück
            </button>
            <h2 style={{ margin: 0, fontSize: 18 }}>Gehen</h2>
          </div>

          {error && <p className="error-text">{error}</p>}

          {/* Was wurde gemacht */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ausgeführte Tätigkeit <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </p>
            <input
              className="input"
              type="text"
              placeholder="Was wurde heute gemacht?"
              value={taetigkeit}
              onChange={(e) => setTaetigkeit(e.target.value)}
            />
          </div>

          {/* Zusatzgerät */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Zusatzgerät / Anbauteil <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {ZUSATZGERAET_VORSCHLAEGE.map((item) => (
                <button
                  key={item}
                  onClick={() => toggleZusatzgeraet(item)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    border: `1px solid ${zusatzgeraetAktiv(item) ? '#22c55e' : '#334155'}`,
                    background: zusatzgeraetAktiv(item) ? '#14532d' : '#1e293b',
                    color: zusatzgeraetAktiv(item) ? '#86efac' : '#94a3b8',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            <input
              className="input"
              type="text"
              placeholder="Weitere oder Freitext …"
              value={zusatzgeraetFreitext}
              onChange={(e) => setZusatzgeraetFreitext(e.target.value)}
            />
          </div>

          <button
            className="button button-out button-primary-action"
            onClick={() => submit('OUT', undefined, undefined, taetigkeit, zusatzgeraetKombiniert())}
          >
            Ausgestempelt
          </button>
        </div>
      </div>
    )
  }

  // --- Hauptansicht ---
  return (
    <div className="screen">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>Mein Stempel</h1>
          <button
            className="button button-secondary"
            style={{ fontSize: 14, padding: '8px 12px' }}
            onClick={logout}
          >
            Abmelden
          </button>
        </div>

        <p style={{ margin: '2px 0 12px', color: '#94a3b8', fontSize: 15 }}>{displayName}</p>

        {error && <p className="error-text">{error}</p>}

        {lines.map((l) => (
          <p
            key={l.eventId}
            className={l.tone === 'error' ? 'error-text' : l.tone === 'ok' ? 'confirmation' : 'hint'}
            style={l.tone === 'ok' ? { fontSize: 16 } : undefined}
          >
            {l.text}
          </p>
        ))}

        {effectiveStatus && (
          <div className={`status-badge ${isCheckedIn ? 'status-in' : 'status-out'}`}>
            <span className="status-dot" />
            {isCheckedIn && lastTime ? (
              <>
                Eingestempelt seit {formatTime(lastTime)}
                <span className="status-duration"> ({formatDuration(lastTime)})</span>
              </>
            ) : (
              'Ausgestempelt'
            )}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          {!isCheckedIn ? (
            <SlideButton
              label="← Gleiten zum Kommen"
              onConfirm={() => {
                setError(null)
                setView('kommen-auftrag')
              }}
            />
          ) : (
            <button
              className="button button-out button-primary-action"
              onClick={() => {
                setError(null)
                setTaetigkeit('')
                setZusatzgeraetChips([])
                setZusatzgeraetFreitext('')
                setView('gehen-confirm')
              }}
            >
              ↑ Gehen
            </button>
          )}
        </div>
      </div>
      {rapportAngebot &&
        angebotGueltig(rapportAngebot) &&
        !isCheckedIn &&
        (auftraege.length === 0 || auftraege.some((a) => a.auftragId === rapportAngebot.auftragId)) && (
          <div className="card" style={{ marginTop: 12 }}>
            <RapportBereich
              employeeId={employeeId}
              angebot={rapportAngebot}
              mitarbeiterName={displayName}
              stempelAusstehend={pendingCount > 0}
              onAngebot={setRapportAngebot}
              erstelleRapport={erstelleRapport}
            />
          </div>
        )}
      <PersonalOutboxBanner pendingCount={pendingCount} />
    </div>
  )
}
